import React, { useEffect, useState } from "react";
import { saveSession } from "react-session-persist";
import { useSelector, shallowEqual } from "react-redux";
import ReactHtmlParser from "react-html-parser";
import { useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import publicIp from "public-ip";
import * as auth from "../_redux/authRedux";
import {
  LOGIN_MUTATION,
  LOGIN_REQUEST_QUERY,
  REQUEST_FOR_OTP,
  VERIFY_OTP_CODE_MUTATION,
} from "../../Administration/_Commons/Constants/Queries/Auth";
import { useScreenLock } from "../../../screenLock";
import LogoutOthersModal from "./LogoutOthersModal"; // update path accordingly
import { encryptEmail } from "../../Helpers/Utilities";
import {
  formatOtpCountdown,
  getOtpExpiryTimestamp,
  getOtpSecondsRemaining,
  hasOtpExpired,
  isValidOtpFormat,
  OTP_CODE_LENGTH,
  sanitizeOtpValue,
} from "../../Helpers/OtpExpiry";

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

const initialValues = {
  username: "",
  password: "",
};

function Login(props) {
  const { intl } = props;
  const screenLock = useScreenLock();
  // const { /* hasAuthToken, */ logout } = props;
  const { otpVerification } = useSelector((state) => state.auth, shallowEqual);
  const [loading, setLoading] = useState(false);
  const [loginDetails, setLoginDetails] = useState({});
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [userLogin] = useMutation(LOGIN_MUTATION);
  const [verifyOtpCode] = useMutation(VERIFY_OTP_CODE_MUTATION);
  const requestLogin = useQuery(LOGIN_REQUEST_QUERY);
  const requestForOtp = useQuery(REQUEST_FOR_OTP);
  const otpRequired = !!otpVerification?.required;
  const otpExpiresAt = otpVerification?.expiresAt || null;
  const [otpSecondsRemaining, setOtpSecondsRemaining] = useState(() => getOtpSecondsRemaining(otpExpiresAt));
  const otpCodeExpired = !!(otpRequired && otpExpiresAt && hasOtpExpired(otpExpiresAt));

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      // .email("Wrong email format")
      .min(3, "Minimum 3 characters")
      .max(50, "Maximum 50 characters")
      .required(
        intl.formatMessage({
          id: "AUTH.VALIDATION.USER_NAME",
        })
      ),
    password: Yup.string()
      .min(3, "Minimum 3 characters")
      .max(50, "Maximum 50 characters")
      .required(
        intl.formatMessage({
          id: "AUTH.VALIDATION.PASSWORD",
        })
      ),
  });

  const OtpCodeSchema = Yup.object().shape({
    code: Yup.string()
      .matches(new RegExp(`^\\d{${OTP_CODE_LENGTH}}$`), `Code should be ${OTP_CODE_LENGTH} digits`)
      .required(
        intl.formatMessage({
          id: "AUTH.VALIDATION.REQUIRED_FIELD",
        })
      ),
  });

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (
      (loginFormik.touched[fieldname] && loginFormik.errors[fieldname]) ||
      (otpCodeFormik.touched[fieldname] && otpCodeFormik.errors[fieldname])
    ) {
      return "is-invalid";
    }

    if (
      (loginFormik.touched[fieldname] && !loginFormik.errors[fieldname]) ||
      (otpCodeFormik.touched[fieldname] && !otpCodeFormik.errors[fieldname])
    ) {
      return "is-valid";
    }

    return "";
  };

  //Added below useEffect to clear username textfield so that OTP can be entered
    useEffect(() => {
      if (otpVerification?.required) {
        otpCodeFormik.resetForm({ values: { code: "" } });
      }
      }, [otpVerification?.required]);
  //Up to here

  useEffect(() => {
    if (!otpRequired || !otpExpiresAt) {
      setOtpSecondsRemaining(0);
      return undefined;
    }

    const syncOtpCountdown = () => {
      setOtpSecondsRemaining(getOtpSecondsRemaining(otpExpiresAt));
    };

    syncOtpCountdown();

    const timerId = window.setInterval(syncOtpCountdown, 1000);

    return () => window.clearInterval(timerId);
  }, [otpExpiresAt, otpRequired]);

  const showOtpVerification = (userEmail, otpUse) => {
    props.createOtpVerification({
      required: true,
      userEmail,
      otpVerification: true,
      otpUse,
      expiresAt: getOtpExpiryTimestamp(),
    });
  };

  const processLogin = async (loginDetails) => {
    const {
      access_token,
      refresh_token,
      user,
      userMenu,
      activeFiscalYear,
      // previousFiscalYear, stages,
      activeStages,
      userRole,
      userVote,
    } = loginDetails;

    let mainMenus = userMenu && userMenu.map((menu) => menu.Main_Menu_ID);
    let mainMenuIds = userMenu && userMenu.map((menu) => menu.Main_Menu_ID && menu.Main_Menu_ID.Main_Menu_ID);
    let menuPaths = [
      "/",
      "/dashboard",
      "/budget",
      "/archives",
      "/user-profile",
      "/user-profile/personal-information",
      "/user-profile/change-password",
      "/logout",
    ];

    mainMenuIds = [...new Set(mainMenuIds)];

    const menuData = mainMenuIds.map((mainMenuId) => {
      const mainMenu = mainMenus.find((mainMenu) => mainMenu.Main_Menu_ID === mainMenuId);
      const mainMenuSubMenus = userMenu.filter((menuData) => menuData.Main_Menu_ID && menuData.Main_Menu_ID.Main_Menu_ID === mainMenuId);
      const sortedMainMenuSubMenus = mainMenuSubMenus.sort((a, b) => a.Sub_Menu_Name.trim().localeCompare(b.Sub_Menu_Name.trim()));
      return {
        ...mainMenu,
        subMenus: sortedMainMenuSubMenus,
      };
    });

    const cleanedMenuData = menuData.map((menu) => {
      if (
        menu &&
        menu.Process_Area &&
        menu.Process_Area.length > 0 &&
        menu.Process_Area_Category &&
        menu.Process_Area_Category.length > 0
      ) {
        const processArea = menu.Process_Area.split(",").map((processArea) => processArea.trim().toLowerCase());
        const subMenus = processArea.map((processArea) =>
          menu.subMenus.map((subMenu) => ({ ...subMenu, Sub_Menu_Path: subMenu.Sub_Menu_Path.replace(/process-area/g, processArea) }))
        );
        const cleanedSubMenus = [].concat.apply([], subMenus);
        const cleanedSubMenusPaths = cleanedSubMenus.map((subMenu) => subMenu.Sub_Menu_Path);
        menuPaths = [...menuPaths, ...cleanedSubMenusPaths];
        return { ...menu, subMenus: cleanedSubMenus };
      } else {
        const subMenus = menu.subMenus.map((subMenu) => subMenu.Sub_Menu_Path);
        menuPaths = [...menuPaths, ...subMenus];
        return menu;
      }
    });

    const sortedMenuData = cleanedMenuData.sort((a, b) => a.Main_Menu_Name.trim().localeCompare(b.Main_Menu_Name.trim()));

    props.createOtpVerification();

    screenLock.setIsLocked(false);

    await saveSession({ suppression: false });

    if (sortedMenuData) props.createMenu(sortedMenuData);
    if (menuPaths) props.createMenuPaths(menuPaths);
    if (access_token && refresh_token) props.login(access_token, refresh_token);
    if (user) props.setUser(user);
    if (activeFiscalYear) props.createActiveFiscalYear(activeFiscalYear);
    // if (previousFiscalYear) props.createPreviousFiscalYear(previousFiscalYear);
    // if (stages) props.createStages(stages);
    if (activeStages) props.createActiveStages(activeStages);
    if (userRole) props.createUserRole(userRole);
    if (userVote) props.createUserVote(userVote);
  };

  const loginFormik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoginDetails({ password: values.password.trim() });
      enableLoading();
      requestLogin
        .refetch({ ipAddress: await publicIp.v4(), username: values.username.trim(), password: values.password.trim() })
        .then(async ({ data: { loginRequest } }) => {
          const { user, otpVerification, userRoleId, userVoteId, Ip_Address, userTypeName, loginMessage } = loginRequest;       
          const loginUserEmail = user && user.Email;
          const encryptedLoginUserEmail = encryptEmail(loginUserEmail);
          //const loginUserPhoneNumber = loginUser && loginUser.Mobile_Number;
          //const encryptedLoginUserPhoneNumber = `+256 XXX XXX X${loginUserPhoneNumber && loginUserPhoneNumber.slice(-2)}`;
          setLoginDetails((prev) => ({
            ...prev,
            user,
            otpVerification,
            userRoleId,
            userVoteId,
            Ip_Address,
            userTypeName,
            loginMessage,
            encryptedLoginUserEmail,
            otpUse: "login",
          }));
          if (loginMessage?.toLowerCase().includes("otp") && user) {
            showOtpVerification(encryptedLoginUserEmail, "login");
            setStatus("Enter OTP");
            setShowSessionModal(true);
            } else if (user && loginMessage?.toLowerCase().includes("another device")) {
                setStatus("You are already logged in on another device");
                setShowSessionModal(true);
                return;
            }
            else {

              console.log({
                          loginMessage,
                          contains: loginMessage?.toLowerCase().includes("another device"),
                          user,
              });
    
            await login(Ip_Address, values.password.trim(), user.User_Name, null, otpVerification, userRoleId, userVoteId, userTypeName)
              .then(async ({ data: { login } }) => {
                await processLogin(login);
              })
              .catch((error) => {
                disableLoading();
                setSubmitting(false);
                const extractedError = JSON.parse(JSON.stringify(error, null, 2));
                const errorMessage = extractedError && extractedError.message;
                setStatus(
                  intl.formatMessage({
                    id: errorMessage,
                  })
                );
              });
          }
          disableLoading();
          setSubmitting(false);
        })
        .catch((error) => {
          disableLoading();
          setSubmitting(false);

          const extractedError = JSON.parse(JSON.stringify(error, null, 2));
          const errorMessage = extractedError && extractedError.message;

          if (errorMessage && errorMessage.trim()) {
            setStatus(
              intl.formatMessage({
                id: errorMessage,
              })
            );
          } else {
            setStatus(
              intl.formatMessage({
                id: "AUTH.VALIDATION.INVALID_LOGIN",
              })
            );
          }
        });
    },
  });

  const login = (ipAddress, password, username, otpNumber, otpVerification, userRoleId, userVoteId, User_Type_Name) => {
  return  userLogin({
      variables: {
        ipAddress,
        password,
        username,
        otpNumber,
        otpVerification,
        userRoleId,
        userVoteId,
        User_Type_Name,
      },
    })     
  };

  const otpCodeFormik = useFormik({
    initialValues,
    validationSchema: OtpCodeSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (otpCodeExpired) {
        disableLoading();
        setSubmitting(false);
        setStatus("The code has expired. Request a new one.");
        return;
      }

      const {
        user: { User_Name },
        Ip_Address,
        otpVerification,
        userRoleId,
        userVoteId,
        User_Type_Name,
        password,
      } = loginDetails;
      enableLoading();

      try {
        const { data: { verifyOtpCode: otpCheck } = {} } = await verifyOtpCode({
          variables: {
            code: values.code.toString(),
            userId: loginDetails?.user?.User_ID,
          },
        });

        if (!otpCheck?.valid) {
          disableLoading();
          setSubmitting(false);
          if (otpCheck?.used) {
            setStatus(`The code ${values.code} provided is already used!`);
          } else if (otpCheck?.expired) {
            setStatus(`The code ${values.code} provided has expired. Please request a new one.`);
          } else {
            setStatus(`The code ${values.code} provided is invalid!`);
          }
          return;
        }
      } catch (error) {
        disableLoading();
        setSubmitting(false);
        const extractedError = JSON.parse(JSON.stringify(error, null, 2));
        setStatus((extractedError && extractedError.message) || "Unable to verify the code right now.");
        return;
      }

      await login(Ip_Address, password, User_Name, values.code.toString(), otpVerification, userRoleId, userVoteId, User_Type_Name)
        .then(async ({ data: { login } }) => {
          await processLogin(login);
        })
        .catch((error) => {
          disableLoading();
          setSubmitting(false);
          const extractedError = JSON.parse(JSON.stringify(error, null, 2));
          const errorMessage = extractedError && extractedError.message;
          setStatus(
            intl.formatMessage({
              id: errorMessage,
            })
          );
        });
    },
  });

  const resendOtp = async () => {
    if (!loginDetails?.user?.User_ID) {
      return;
    }

    enableLoading();
    otpCodeFormik.resetForm();

    try {
      const otpUse = otpVerification?.otpUse || loginDetails.otpUse || "login";
      const { data: { requestForOtp: otpRequest } = {} } = await requestForOtp.refetch({
        otpUse,
        User_ID: loginDetails.user.User_ID,
        User_Name: loginDetails.user.User_Name,
        Email: loginDetails.user.Email,
        Mobile_Number: loginDetails.user.Mobile_Number || "000000",
      });

      if (otpRequest?.User_ID) {
        showOtpVerification(loginDetails.encryptedLoginUserEmail, otpUse);
      }
    } catch (error) {
      otpCodeFormik.setStatus(error?.message || "Unable to send a new code right now.");
    } finally {
      disableLoading();
    }
  };

  const handleOtpCodeChange = (event) => {
    otpCodeFormik.setFieldValue("code", sanitizeOtpValue(event.target.value));
  };

  const confirmLogout = async () => {
    requestForOtp
      .refetch({
        otpUse: "logging out other devices",
        User_ID: loginDetails.user.User_ID,
        User_Name: loginDetails.user.User_Name,
        Email: loginDetails.user.Email,
        Mobile_Number: loginDetails.user.Mobile_Number || "000000",
      })
      .then(async ({ data: { requestForOtp } }) => {
        if (requestForOtp?.User_ID) {
          showOtpVerification(loginDetails.encryptedLoginUserEmail, "logging out other devices");
        }
      });
  };

  return (
    <>
      {otpVerification && otpVerification.required && otpVerification.userEmail ? (
        <div className="login-form login-forgot" style={{ display: "block" }}>
          <div className="text-center mb-10 mb-lg-20">
            <h3 className="font-size-h1">Enter Code</h3>
            <div className="text-muted font-weight-bold">
              {/* userEmail */}
              {/* We texted the code to your phone <span className="text-dark">{otpVerification.userPhoneNumber}</span>. */}
              A One-time PIN has been sent to your email <span className="text-dark">{otpVerification.userEmail}</span>. 
              <br />
              Please enter OTP to sign in.
              <br />
              <span className={otpCodeExpired ? "text-danger" : "text-dark"}>
                {otpCodeExpired
                  ? "This code has expired. Request a new one to continue."
                  : `Code expires in ${formatOtpCountdown(otpSecondsRemaining)}.`}
              </span>
            </div>
          </div>
          <form
            onSubmit={otpCodeFormik.handleSubmit}
            className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp"
          >
            {otpCodeFormik.status && (
              <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                <div className="alert-text font-weight-bold">{otpCodeFormik.status}</div>
              </div>
            )}
            <div className="form-group fv-plugins-icon-container">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("code")}`}
                name="code"
                placeholder="Code"
                disabled={otpCodeExpired}
                value={otpCodeFormik.values.code}
                onBlur={otpCodeFormik.handleBlur}
                onChange={handleOtpCodeChange}
              />
              {otpCodeFormik.touched.code && otpCodeFormik.errors.code ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block text-danger">{otpCodeFormik.errors.code}</div>
                </div>
              ) : null}
            </div>
            <div className="form-group d-flex flex-wrap flex-center">
              <button
                type="button"
                id="kt_login_forgot_cancel"
                className="btn btn-outline-dark font-weight-bold px-9 py-4 my-3 mx-4"
                onClick={() => {
                  otpCodeFormik.resetForm();
                  props.createOtpVerification();
                }}
              >
                Cancel
              </button>

              <button
                id="kt_login_forgot_submit"
                type="submit"
                className="btn btn-primary font-weight-bold px-9 py-4 my-3 mx-4"
                disabled={otpCodeFormik.isSubmitting || otpCodeExpired || !isValidOtpFormat(otpCodeFormik.values.code)}
              >
                <span>Verify</span>
                {/*loading && <span className="ml-3 spinner spinner-white"></span>*/}
              </button>

              <button
                type="button"
                className="btn btn-outline-primary font-weight-bold px-9 py-4 my-3 mx-4"
                onClick={resendOtp}
              >
                <span>Request New Code</span>
                {/*loading && <span className="ml-3 spinner spinner-primary"></span>*/}
              </button>
            
            </div>
          </form>
        </div>
      ) : (
        <div className="login-form login-signin" id="kt_login_signin_form">
          {/* begin::Head */}
          <div className="text-center mb-10 mb-lg-20">
            <h3 className="font-size-h1">
              <FormattedMessage id="AUTH.LOGIN.TITLE" />
            </h3>
            <p className="text-muted font-weight-bold">Enter your username and password</p>
          </div>
          {/* end::Head */}

          {/*begin::Form*/}
          <form onSubmit={loginFormik.handleSubmit} className="form fv-plugins-bootstrap fv-plugins-framework" autoComplete="off">
            {loginFormik.status ? (
              <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                <div className="alert-text font-weight-bold">{ReactHtmlParser(loginFormik.status)}</div>
              </div>
            ) : (
              <div>
             
              </div>
            )}

            <div className="form-group fv-plugins-icon-container">
              <input
                placeholder="Username"
                type="text"
                className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("username")}`}
                name="username"
                {...loginFormik.getFieldProps("username")}
              />
              {loginFormik.touched.username && loginFormik.errors.username ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{loginFormik.errors.username}</div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <input
                placeholder="Password"
                type="password"
                className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("password")}`}
                name="password"
                {...loginFormik.getFieldProps("password")}
              />
              {loginFormik.touched.password && loginFormik.errors.password ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{loginFormik.errors.password}</div>
                </div>
              ) : null}
            </div>
            <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
              <Link to="/auth/forgot-password" className="text-dark-50 text-hover-primary my-3 mr-2" id="kt_login_forgot">
                <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
              </Link>
              <button
                id="kt_login_signin_submit"
                type="submit"
                disabled={loginFormik.isSubmitting}
                className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}
              >
                <span>Sign In</span>
                {loading && <span className="ml-3 spinner spinner-white"></span>}
              </button>
            </div>
          </form>
          {/*end::Form*/}
          <LogoutOthersModal
            show={showSessionModal}
            //show={true}
            onConfirm={confirmLogout}
            onCancel={() => {
              setShowSessionModal(false);
            }}
          />
        </div>
      )}
    </>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
